import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { updatePassword } from 'firebase/auth';
import { useAuthWithMasquerade } from '../hooks/useAuthWithMasquerade';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../translations';
import { Language } from '../contexts/LanguageContext';
import { getCachedCollection, updateCachedDocument } from '../utils/firebaseUtils';
import { where } from 'firebase/firestore';
import { uploadProfilePicture, deleteProfilePicture, validateProfilePicture } from '../utils/profilePictureUtils';
import { PhotoIcon, CheckIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

interface UserProfile {
  id: string;
  name?: string;
  email: string;
  createdAt: string;
  language?: Language;
  status: string;
  isAdmin: boolean;
  uid: string;
  updatedAt: string;
  profilePictureUrl?: string;
}

interface ClassContract {
  id: string;
  contractUrl: string;
  courseType: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  scheduleType: 'single' | 'multiple';
  schedules?: any[];
}

const logProfile = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PROFILE] ${message}`, data ? data : '');
  }
};

export const Profile = () => {
  const { currentUser, isMasquerading, masqueradingAs } = useAuthWithMasquerade();
  const { language, setLanguage } = useLanguage();
  const t = useTranslation(language);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updateSuccessful, setUpdateSuccessful] = useState(false);
  const [contracts, setContracts] = useState<ClassContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoize the filtered contracts to prevent unnecessary re-renders
  const memoizedContracts = useMemo(() => {
    return contracts.filter(c => c && c.contractUrl);
  }, [contracts]);

  const fetchProfile = useCallback(async () => {
    if (!currentUser) {
      logProfile('No current user found, returning early');
      setLoading(false);
      return;
    }

    try {
      // If masquerading, use the masqueraded user's ID
      const userIdToFetch = isMasquerading && masqueradingAs ? masqueradingAs.uid || masqueradingAs.id : currentUser.uid;
      
      if (!userIdToFetch) {
        logProfile('No user ID to fetch, returning early');
        setLoading(false);
        return;
      }
      
      logProfile('Attempting to fetch user documents for uid:', userIdToFetch);
      const users = await getCachedCollection<UserProfile>('users', [
        where('uid', '==', userIdToFetch)
      ], { userId: currentUser.uid });
      
      if (!users || users.length === 0) {
        logProfile('No user document found for uid:', userIdToFetch);
        throw new Error('User document not found');
      }

      const userDoc = users[0];
      logProfile('Found user document:', {
        id: userDoc.id,
        name: userDoc.name,
        email: userDoc.email,
        language: userDoc.language
      });
      
      setProfile(userDoc);
      setName(userDoc.name || '');
      setSelectedLanguage(userDoc.language || 'en');
      setProfilePicturePreview(userDoc.profilePictureUrl || null);
      
      logProfile('State updated with profile data:', {
        profileSet: !!userDoc,
        nameSet: userDoc.name || '',
        languageSet: userDoc.language || 'en',
        profilePictureUrl: userDoc.profilePictureUrl
      });
      
      setLoading(false);
      
      // Fetch contracts for this user
      await fetchContracts(userDoc.email);
    } catch (error) {
      logProfile('Profile fetch - Error:', error);
      if (error instanceof Error) {
        logProfile('Profile fetch - Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      setLoading(false);
    }
  }, [currentUser, isMasquerading, masqueradingAs]);
  
  const fetchContracts = async (email: string) => {
    if (!email) {
      setLoadingContracts(false);
      return;
    }
    
    setLoadingContracts(true);
    try {
      logProfile('Querying contracts for user:', email);
      
      // Fetch classes without a limit since the CacheOptions doesn't support it
      const classes = await getCachedCollection<ClassContract>(
        'classes', 
        [where('studentEmails', 'array-contains', email)], 
        { userId: currentUser?.uid }
      );
      
      // Set contracts directly without filtering here
      // The filtering is now handled by the memoizedContracts
      logProfile('Found classes:', classes.length);
      setContracts(classes);
      setLoadingContracts(false);
    } catch (error) {
      logProfile('Contracts fetch - Error:', error);
      if (error instanceof Error) {
        logProfile('Contracts fetch - Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      // Ensure we set loading to false and set contracts to empty array on error
      setContracts([]);
      setLoadingContracts(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, isMasquerading, masqueradingAs?.id]);

  // Add new useEffect to handle success message after language updates
  useEffect(() => {
    if (updateSuccessful) {
      setSuccess(t.profileUpdated);
      setUpdateSuccessful(false);
    }
  }, [language, t, updateSuccessful]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUpdateSuccessful(false);

    if (!currentUser) return;

    try {
      logProfile('Attempting to fetch current user document for uid:', currentUser.uid);
      const users = await getCachedCollection<UserProfile>('users', [
        where('uid', '==', currentUser.uid)
      ], { userId: currentUser.uid });
      
      if (!users || users.length === 0) {
        logProfile('Profile update - User document not found for uid:', currentUser.uid);
        throw new Error('User document not found');
      }

      const userDoc = users[0];
      logProfile('Profile update - Current document data:', userDoc);
      
      // Only include fields that have actually changed and have valid values
      const updates: { name?: string; language?: Language } = {};
      
      if (name !== userDoc.name && name.trim() !== '') {
        updates.name = name;
      }
      if (selectedLanguage !== userDoc.language && selectedLanguage) {
        updates.language = selectedLanguage;
      }

      logProfile('Profile update - Current user:', currentUser.uid);
      logProfile('Profile update - Updates to apply:', updates);
      logProfile('Profile update - Fields being updated:', Object.keys(updates));

      if (Object.keys(updates).length === 0) {
        logProfile('Profile update - No changes detected or no valid values to update, skipping update');
        setUpdateSuccessful(true);
        return;
      }

      logProfile('Profile update - Attempting to update document');
      try {
        // Always update using the document ID from the query result
        await updateCachedDocument('users', userDoc.id, updates, { userId: currentUser.uid });
        logProfile('Profile update - Document updated successfully');
        
        if (selectedLanguage !== userDoc.language) {
          logProfile('Profile update - Attempting to update language context');
          await setLanguage(selectedLanguage);
          logProfile('Profile update - Language context updated successfully');
        }

        // Only attempt to update password if not masquerading and password fields are filled
        if (newPassword && !isMasquerading && 'updatePassword' in currentUser) {
          if (newPassword !== confirmPassword) {
            setError(t.error);
            return;
          }
          await updatePassword(currentUser, newPassword);
        } else if (newPassword && isMasquerading) {
          // Show a message that password can't be updated while masquerading
          setError('Password cannot be updated while impersonating a user');
          return;
        }

        await fetchProfile();
        setNewPassword('');
        setConfirmPassword('');
        setUpdateSuccessful(true);
      } catch (docError) {
        logProfile('Profile update - Document update error:', docError);
        if (docError instanceof Error) {
          logProfile('Profile update - Error details:', {
            message: docError.message,
            name: docError.name,
            stack: docError.stack
          });
          logProfile('Profile update - Update operation details:', {
            documentId: userDoc.id,  // Log the actual document ID being used
            currentData: userDoc,
            attemptedUpdates: updates,
            userId: currentUser.uid,
            userEmail: currentUser.email
          });
        }
        throw docError;
      }
    } catch (error) {
      logProfile('Profile update - Error:', error);
      if (error instanceof Error) {
        logProfile('Profile update - Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      setError('Failed to update profile');
    }
  };
  
  // Helper function to get day name
  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  // Handle profile picture file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateProfilePicture(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle profile picture upload
  const handleUploadPicture = async () => {
    if (!currentUser || !selectedFile) return;

    setUploadingPicture(true);
    setError('');

    try {
      const userId = isMasquerading && masqueradingAs ? masqueradingAs.uid || masqueradingAs.id : currentUser.uid;
      if (!userId) {
        throw new Error('User ID not found');
      }

      // If there's an existing picture, delete it first (overwrite)
      if (profile?.profilePictureUrl) {
        try {
          await deleteProfilePicture(userId, profile.profilePictureUrl);
        } catch (deleteError) {
          // Continue even if delete fails (might not exist)
          logProfile('Error deleting old profile picture (may not exist):', deleteError);
        }
      }

      // Upload new picture
      const downloadUrl = await uploadProfilePicture(userId, selectedFile);

      // Update Firestore
      const users = await getCachedCollection<UserProfile>('users', [
        where('uid', '==', userId)
      ], { userId: currentUser.uid });

      if (users && users.length > 0) {
        await updateCachedDocument('users', users[0].id, { profilePictureUrl: downloadUrl }, { userId: currentUser.uid });
      }

      setSuccess(t.profilePictureUploaded);
      setSelectedFile(null);
      // Reset file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await fetchProfile();
    } catch (error) {
      logProfile('Error uploading profile picture:', error);
      setError(t.profilePictureError);
      if (error instanceof Error) {
        setError(error.message || t.profilePictureError);
      }
    } finally {
      setUploadingPicture(false);
    }
  };

  // Handle profile picture deletion
  const handleDeletePicture = async () => {
    if (!currentUser) return;

    if (!window.confirm(t.confirmDeleteProfilePicture)) {
      return;
    }

    setUploadingPicture(true);
    setError('');

    try {
      const userId = isMasquerading && masqueradingAs ? masqueradingAs.uid || masqueradingAs.id : currentUser.uid;
      if (!userId) {
        throw new Error('User ID not found');
      }

      if (profile?.profilePictureUrl) {
        await deleteProfilePicture(userId, profile.profilePictureUrl);

        // Update Firestore
        const users = await getCachedCollection<UserProfile>('users', [
            where('uid', '==', userId)
          ], { userId: currentUser.uid });

        if (users && users.length > 0) {
          await updateCachedDocument('users', users[0].id, { profilePictureUrl: null }, { userId: currentUser.uid });
        }

        setSuccess(t.profilePictureDeleted);
        setProfilePicturePreview(null);
        setSelectedFile(null);
        // Reset file input to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await fetchProfile();
      } else {
        setProfilePicturePreview(null);
        setSelectedFile(null);
        // Reset file input to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

    } catch (error) {
      logProfile('Error deleting profile picture:', error);
      setError(t.profilePictureError);
      if (error instanceof Error) {
        setError(error.message || t.profilePictureError);
      }
    } finally {
      setUploadingPicture(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative min-h-screen bg-transparent" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise' x='0' y='0'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")`,
      backgroundAttachment: 'fixed'
    }}>
      <div className="py-6 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:flex-col">
          <div className="mb-8">
            <h3 className="text-2xl font-medium leading-6 text-gray-900">{t.profile}</h3>
            {isMasquerading && masqueradingAs && (
              <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-md p-3">
                <p className="text-sm text-indigo-700">
                  You are viewing {masqueradingAs.name || masqueradingAs.email}'s profile as an administrator.
                  Some actions like password changes are disabled while impersonating.
                </p>
              </div>
            )}
            <p className="mt-2 text-sm text-gray-600">
              {t.updateProfile}
            </p>
          </div>

          <div className="w-full">
            <div className="shadow sm:rounded-lg sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}
                {success && (
                  <div className="rounded-md bg-green-50 p-4">
                    <div className="text-sm text-green-700">{success}</div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Profile Picture Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.profilePicture}
                    </label>
                    <div className="flex items-center space-x-6">
                      <div className="flex-shrink-0 relative group">
                        {profilePicturePreview ? (
                          <>
                            <img
                              src={profilePicturePreview}
                              alt="Profile preview"
                              className="h-24 w-24 rounded-full object-cover border-2 border-gray-300"
                            />
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute -top-1 -left-1 p-1.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-transform hover:scale-110 pointer-events-auto z-10"
                                title={t.changeProfilePicture}
                              >
                                <PencilIcon className="h-4 w-4 text-gray-700" />
                              </button>
                              <button
                                type="button"
                                onClick={handleDeletePicture}
                                className="absolute -top-1 -right-1 p-1.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-transform hover:scale-110 pointer-events-auto z-10"
                                title={t.deleteProfilePicture}
                              >
                                <TrashIcon className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          </>
                        ) : profile?.profilePictureUrl ? (
                          <>
                            <img
                              src={profile.profilePictureUrl}
                              alt="Profile"
                              className="h-24 w-24 rounded-full object-cover border-2 border-gray-300"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute -top-1 -left-1 p-1.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-transform hover:scale-110 pointer-events-auto z-10"
                                title={t.changeProfilePicture}
                              >
                                <PencilIcon className="h-4 w-4 text-gray-700" />
                              </button>
                              <button
                                type="button"
                                onClick={handleDeletePicture}
                                disabled={uploadingPicture}
                                className="absolute -top-1 -right-1 p-1.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-transform hover:scale-110 pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed z-10"
                                title={t.deleteProfilePicture}
                              >
                                <TrashIcon className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="h-24 w-24 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                            <PhotoIcon className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        {!profile?.profilePictureUrl && !selectedFile && (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="group inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                            >
                              <PhotoIcon className="h-4 w-4 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                              <span>{t.uploadProfilePicture}</span>
                            </button>
                          </div>
                        )}
                        {selectedFile && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-1">{t.selectImage}</p>
                                <p className="text-xs text-gray-500">
                                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={handleUploadPicture}
                                disabled={uploadingPicture}
                                className="group inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-700 hover:border-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {uploadingPicture ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                    <span>{t.saving}</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckIcon className="h-3 w-3" />
                                    <span>{t.save}</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      {t.name}
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      {t.email}
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={profile?.email}
                      disabled
                      className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>

                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                      {t.language}
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value as Language)}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                      <option value="en">English</option>
                      <option value="pt-BR">Português</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                      {t.newPassword}
                    </label>
                    <input
                      type="password"
                      name="new-password"
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isMasquerading}
                      className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isMasquerading ? 'bg-gray-50' : ''}`}
                    />
                    {isMasquerading && (
                      <p className="mt-1 text-xs text-gray-500">Password changes are disabled while impersonating a user.</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      {t.confirmPassword}
                    </label>
                    <input
                      type="password"
                      name="confirm-password"
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isMasquerading}
                      className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isMasquerading ? 'bg-gray-50' : ''}`}
                    />
                  </div>

                  {/* Contracts Section */}
                  {!profile?.isAdmin && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">{t.yourContracts}</h4>
                      {loadingContracts ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : memoizedContracts && memoizedContracts.length > 0 ? (
                        <div className="bg-gray-50 shadow rounded-lg overflow-hidden">
                          <ul className="divide-y divide-gray-200">
                            {memoizedContracts.slice(0, 10).map((contract) => (
                              <li key={contract.id} className="px-4 py-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {contract.courseType || 'Class'} Class
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {contract.scheduleType === 'single' ? (
                                        <>
                                          {getDayName(contract.dayOfWeek)} at {contract.startTime || '00:00'} - {contract.endTime || '00:00'}
                                        </>
                                      ) : (
                                        <>
                                          Multiple days schedule
                                        </>
                                      )}
                                    </p>
                                  </div>
                                  {contract.contractUrl && (
                                    <a
                                      href={contract.contractUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                      View Contract
                                    </a>
                                  )}
                                </div>
                              </li>
                            ))}
                            {memoizedContracts.length > 10 && (
                              <li className="px-4 py-2 text-center text-sm text-gray-500">
                                Showing 10 of {memoizedContracts.length} contracts
                              </li>
                            )}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No contracts available.</p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {t.save}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 
